import React from 'react';
import * as consts from 'config/consts';
import NumberLabel from 'components/features/utils/NumberLabel';
import {
  DataFetchingState,
  getDataFetchingState,
} from 'components/features/home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import { getDataFetchingTextStyle_ } from 'components/features/home/PortfolioRatioStuffing/PortfolioRatioStuffingList';
import { Side, SideLabel } from 'components/features/utils/side';
import {
  isForeignStockTypes,
  isQtyHiddenProductType,
  productTypesToString,
  productTypesToUnit,
} from 'components/features/utils/productTypes';
import { isGoSymbolDetail } from 'components/features/utils/util';
import { formatNumber } from 'utils/formatter';
import {
  AccountType,
  AccountTypeLabel,
} from 'components/features/utils/accountType';
import Icon from 'components/commons/icon/Icon';
import { OrderItemProps } from '../DailyReport';
import { getSignColor } from '../../yearly-report/YearlyReport';
import { addSignCommaFormat, getSign } from '../../utils/formatter';

const TradeItemSymbolName = ({
  children,
  textColor,
}: {
  children: string;
  textColor?: string;
}) => {
  return (
    <span
      className={`text-sm font-bold mr-2 ${textColor ??
        'text-bluegray-700'} items-center line-clamp-2`}>
      {children}
    </span>
  );
};

const TradeItemQuantity = ({
  qty,
  className,
  productType,
}: {
  qty: number | null;
  className: string;
  productType: number;
}) => {
  return (
    <>
      <div className={'text-sm flex items-center ' + className}>
        {qty === null
          ? '---'
          : formatNumber(qty, {
              unitName: '',
              noValueText: '---',
            })}
        <div className="text-xs ml-1">{productTypesToUnit(productType)}</div>
      </div>
    </>
  );
};

type TradeItemProps = {
  order: OrderItemProps;
  borderTop: React.ReactElement | false;
  onTradeItemClick: (symbol: string) => void;
};

export const NewTradeItem = ({
  borderTop,
  onTradeItemClick: onTradeItemClick_,
  order,
}: TradeItemProps & { targetDate: Date | null }): React.ReactElement => {
  const dataFetchingState: DataFetchingState = getDataFetchingState(
    order.symbol,
    order.productType === consts.productTypes.投資信託
      ? consts.AssetType['投資信託']
      : consts.AssetType['その他']
  );
  const onTradeItemClick = (symbol: string, productType: number) => {
    if (
      dataFetchingState === 'Fetched' &&
      isGoSymbolDetail(symbol, productType)
    )
      return onTradeItemClick_(symbol);
  };
  const getDataFetchingTextStyle = getDataFetchingTextStyle_(dataFetchingState);

  const isProfitAndLossHidden =
    order.cashMargin === consts.CashMargin.現物 &&
    order.side === consts.Side.買 &&
    order.profitOrLoss === null;

  const isForeign = isForeignStockTypes(order.productType);

  return (
    <>
      {borderTop}
      <div
        className="flex items-center mx-3 py-4 cursor-pointer"
        onClick={(): void => onTradeItemClick(order.symbol, order.productType)}>
        <div className="w-full">
          <div className="h-10 flex flex-grow items-center">
            <SideLabel side={order.side as Side} />
            <div className="flex flex-col ml-2">
              <AccountTypeLabel
                accountType={order.accountType as AccountType}
              />
              <div className="flex flex-row items-center text-xs">
                <div
                  className={`ml-1 ${getDataFetchingTextStyle(
                    'text-bluegray-700'
                  )}`}>
                  {productTypesToString(order.productType, order.cashMargin)}
                </div>
                {!isQtyHiddenProductType(order.productType) ? (
                  <TradeItemQuantity
                    {...order}
                    className={`ml-4 mr-2 ${getDataFetchingTextStyle(
                      'text-bluegray-700'
                    )}`}
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
          <div className="w-full flex flex-row mt-3 items-end">
            <TradeItemSymbolName textColor={getDataFetchingTextStyle('')}>
              {order.symbolName}
            </TradeItemSymbolName>
            {!isProfitAndLossHidden && (
              <div
                className={`${getDataFetchingTextStyle(
                  getSignColor(getSign(order.profitOrLoss ?? 0))
                )} flex items-center ml-auto flex-shrink text-sm font-bold`}
                style={{ whiteSpace: 'nowrap' }}>
                {addSignCommaFormat(order.profitOrLoss)}
                <div className="unit ml-1">円</div>
              </div>
            )}
          </div>
          {isForeign && !isProfitAndLossHidden && (
            <div className="flex flex-grow items-center">
              <NumberLabel
                value={order.profitOrLossUSD}
                unitName={order.currencyUnit ?? 'USD'}
                bracketType={consts.BracketType.round}
                className="ml-auto"
                classNames={[
                  'text-xs ' + getDataFetchingTextStyle(''),
                  'text-xs ml-1 ' + getDataFetchingTextStyle(''),
                ]}
                isDispalyNoValueUnit
              />
            </div>
          )}
        </div>
        <Icon
          name="chevron-right"
          className={`text-2xl pl-4 ${
            dataFetchingState === 'Fetched' &&
            isGoSymbolDetail(order.symbol, order.productType)
              ? 'text-bluegray-500'
              : 'text-transparent'
          }`}
        />
      </div>
    </>
  );
};
